import TicketsAPI from '../../api/tickets';

const sortTicketRecords = records => {
  return records.slice().sort((left, right) => {
    const leftLastActivity = left.last_activity_at || 0;
    const rightLastActivity = right.last_activity_at || 0;

    if (leftLastActivity !== rightLastActivity) {
      return rightLastActivity - leftLastActivity;
    }

    return (right.ticket?.id || 0) - (left.ticket?.id || 0);
  });
};

const upsertTicketRecord = (
  $state,
  record,
  { incrementAllCount = false } = {}
) => {
  if (!record?.ticket?.id) {
    return false;
  }

  const existingIndex = $state.records.findIndex(
    currentRecord => currentRecord.ticket?.id === record.ticket.id
  );

  if (existingIndex === -1) {
    $state.records = sortTicketRecords([...$state.records, record]);
    if (incrementAllCount) {
      $state.meta.allCount += 1;
    }
    return true;
  }

  $state.records[existingIndex] = {
    ...$state.records[existingIndex],
    ...record,
  };
  $state.records = sortTicketRecords($state.records);
  return false;
};

const adjustStatusCounts = ($state, previousStatus, nextStatus) => {
  if (previousStatus === nextStatus) {
    return;
  }

  const countKeys = {
    submitted: 'submittedCount',
    in_progress: 'inProgressCount',
    resolved: 'resolvedCount',
  };

  if (countKeys[previousStatus]) {
    $state.meta[countKeys[previousStatus]] = Math.max(
      0,
      ($state.meta[countKeys[previousStatus]] || 0) - 1
    );
  }

  if (countKeys[nextStatus]) {
    $state.meta[countKeys[nextStatus]] =
      ($state.meta[countKeys[nextStatus]] || 0) + 1;
  }
};

export const state = {
  records: [],
  currentTicket: null,
  meta: {
    allCount: 0,
    openCount: 0,
    submittedCount: 0,
    inProgressCount: 0,
    resolvedCount: 0,
    currentPage: 1,
  },
  filters: {
    email: '',
    status: 'all',
  },
  uiFlags: {
    isFetching: false,
    isFetchingDetail: false,
    isUpdating: false,
    isAllTicketsLoaded: false,
  },
};

export const getters = {
  getTickets: $state => $state.records,
  getCurrentTicket: $state => $state.currentTicket,
  getMeta: $state => $state.meta,
  getFilters: $state => $state.filters,
  getUIFlags: $state => $state.uiFlags,
};

export const mutations = {
  setMeta($state, meta = {}) {
    $state.meta = {
      allCount: meta?.all_count || 0,
      openCount: meta?.open_count || 0,
      submittedCount: meta?.submitted_count || 0,
      inProgressCount: meta?.in_progress_count || 0,
      resolvedCount: meta?.resolved_count || 0,
      currentPage: meta?.current_page || 1,
    };
  },
  setTickets($state, { payload = [], meta, append = false }) {
    $state.records = sortTicketRecords(
      append ? [...$state.records, ...payload] : payload
    );
    $state.meta = {
      allCount: meta?.all_count || 0,
      openCount: meta?.open_count || 0,
      submittedCount: meta?.submitted_count || 0,
      inProgressCount: meta?.in_progress_count || 0,
      resolvedCount: meta?.resolved_count || 0,
      currentPage: meta?.current_page || 1,
    };
    $state.uiFlags.isAllTicketsLoaded =
      payload.length === 0 || $state.records.length >= $state.meta.allCount;
  },
  setFilters($state, filters = {}) {
    $state.filters = {
      ...$state.filters,
      ...filters,
    };
  },
  setCurrentTicket($state, ticket) {
    $state.currentTicket = ticket;
  },
  clearTickets($state) {
    $state.records = [];
    $state.uiFlags.isAllTicketsLoaded = false;
  },
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
  updateTicketInList($state, updatedTicket) {
    const idx = $state.records.findIndex(
      r => r.ticket?.id === updatedTicket.id
    );
    if (idx !== -1 && $state.records[idx].ticket) {
      const previousStatus = $state.records[idx].ticket.status;
      $state.records[idx] = {
        ...$state.records[idx],
        ticket: updatedTicket,
      };
      if (previousStatus !== updatedTicket.status) {
        adjustStatusCounts($state, previousStatus, updatedTicket.status);
        if (
          previousStatus === 'resolved' &&
          updatedTicket.status !== 'resolved'
        ) {
          $state.meta.openCount += 1;
        } else if (
          previousStatus !== 'resolved' &&
          updatedTicket.status === 'resolved'
        ) {
          $state.meta.openCount = Math.max(0, $state.meta.openCount - 1);
        }
      }
    }
    if ($state.currentTicket?.id === updatedTicket.id) {
      $state.currentTicket = updatedTicket;
    }
    $state.records = sortTicketRecords($state.records);
  },
  addRealtimeCreatedTicket($state, record) {
    const created = upsertTicketRecord($state, record, {
      incrementAllCount: true,
    });
    if (created && record.ticket.status !== 'resolved') {
      $state.meta.openCount += 1;
    }
    if (created) {
      adjustStatusCounts($state, null, record.ticket.status);
    }
  },
  applyRealtimeTicketUpdate($state, record) {
    const ticketId = record.ticket?.id;
    const existingRecord = $state.records.find(r => r.ticket?.id === ticketId);
    const previousStatus = existingRecord?.ticket?.status;
    const nextStatus = record.ticket?.status;

    upsertTicketRecord($state, record);
    adjustStatusCounts($state, previousStatus, nextStatus);

    if (previousStatus && previousStatus !== nextStatus) {
      if (previousStatus === 'resolved' && nextStatus !== 'resolved') {
        $state.meta.openCount += 1;
      } else if (previousStatus !== 'resolved' && nextStatus === 'resolved') {
        $state.meta.openCount = Math.max(0, $state.meta.openCount - 1);
      }
    }

    if ($state.currentTicket?.id === ticketId) {
      $state.currentTicket = {
        ...$state.currentTicket,
        ...record.ticket,
      };
    }
  },
};

export const actions = {
  async fetchTickets(
    { commit, state: currentState },
    { page = 1, append = false, email, status } = {}
  ) {
    commit('setUIFlag', { isFetching: true });
    try {
      const params = {
        page,
        email: email ?? currentState.filters.email,
        status: status ?? currentState.filters.status,
      };
      const { data } = await TicketsAPI.getTickets(params);
      commit('setTickets', { ...data.data, append });
    } catch (error) {
      // ignore
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchTicketsMeta({ commit, state: currentState }, filters = {}) {
    try {
      const { data } = await TicketsAPI.getTickets({
        page: 1,
        email: filters.email ?? currentState.filters.email,
        status: filters.status ?? currentState.filters.status,
      });
      commit('setMeta', data.data?.meta);
    } catch (error) {
      // ignore
    }
  },

  clear({ commit }) {
    commit('clearTickets');
  },

  async fetchTicketDetail({ commit }, id) {
    commit('setUIFlag', { isFetchingDetail: true });
    try {
      const { data } = await TicketsAPI.getTicket(id);
      commit('setCurrentTicket', data.ticket);
      return data;
    } catch (error) {
      // ignore
    } finally {
      commit('setUIFlag', { isFetchingDetail: false });
    }
    return null;
  },

  async translateTicket({ commit }, id) {
    commit('setUIFlag', { isFetchingDetail: true });
    try {
      const { data } = await TicketsAPI.translateTicket(id);
      if (data.ticket) {
        commit('setCurrentTicket', data.ticket);
        commit('updateTicketInList', data.ticket);
      }
      return { success: true, data };
    } catch (error) {
      const errorData = error.response?.data || {};
      return {
        success: false,
        error: errorData.error || 'Translation failed',
        retryable: errorData.retryable || false,
      };
    } finally {
      commit('setUIFlag', { isFetchingDetail: false });
    }
  },

  async updateTicket({ commit }, { id, ...params }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const { data } = await TicketsAPI.updateTicket(id, params);
      commit('updateTicketInList', data);
      return data;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  handleTicketCreated({ commit }, ticketRecord) {
    commit('addRealtimeCreatedTicket', ticketRecord);
  },

  handleTicketUpdated({ commit }, ticketRecord) {
    commit('applyRealtimeTicketUpdate', ticketRecord);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
